/* ═══════════════════════════════════════════════
   RUGS GALLERY — Theme JavaScript
   ═══════════════════════════════════════════════ */

/* ── Translated strings ────────────────────────
   layout/theme.liquid fills window.RGTheme.strings from the locale files.
   This fallback only matters if that block failed to render. */

const t = (key, fallback) =>
  (window.RGTheme &&
    window.RGTheme.strings &&
    window.RGTheme.strings[key]) ||
  fallback ||
  '';

/* ── Focus trap + modal controller ─────────────
   Shared by the size-consultation modal and any other [data-modal].
   Handles Escape, focus placement on open and focus restoration on close. */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const RGModal = {
  _openEl: null,
  _lastFocus: null,

  open(id) {
    const el = typeof id === 'string' ? document.getElementById(id) : id;
    if (!el) return;

    this._lastFocus = document.activeElement;
    this._openEl = el;

    el.style.display = 'flex';
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rg-scroll-locked');

    // Prefer an error or success banner so screen readers land on the outcome
    const target =
      el.querySelector('[data-form-status]') ||
      el.querySelector(FOCUSABLE);
    if (target) target.focus({ preventScroll: true });
  },

  close() {
    const el = this._openEl;
    if (!el) return;

    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rg-scroll-locked');
    this._openEl = null;

    if (this._lastFocus && this._lastFocus.isConnected) {
      this._lastFocus.focus({ preventScroll: true });
    }
    this._lastFocus = null;
  },

  isOpen() {
    return Boolean(this._openEl);
  },

  _trap(event) {
    const el = this._openEl;
    if (!el || event.key !== 'Tab') return;

    const items = Array.from(el.querySelectorAll(FOCUSABLE)).filter(
      (node) => node.offsetParent !== null
    );
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  },
};

window.RGModal = RGModal;

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-modal-open]');
  if (opener) {
    event.preventDefault();
    RGModal.open(opener.dataset.modalOpen);
    return;
  }

  if (event.target.closest('[data-modal-close]')) {
    event.preventDefault();
    RGModal.close();
    return;
  }

  // Click on the backdrop itself, not the panel
  if (event.target.matches('[data-modal]')) RGModal.close();
});

document.addEventListener('keydown', (event) => {
  if (!RGModal.isOpen()) return;
  if (event.key === 'Escape') RGModal.close();
  else RGModal._trap(event);
});

/* ── WhatsApp hand-off ─────────────────────────
   Secondary action beside a real Shopify form. It never claims the request
   was submitted, and when the popup is blocked it exposes a manual link
   instead of failing silently. */

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wa-share]');
  if (!button) return;

  event.preventDefault();

  const form = button.closest('form');
  const number = button.dataset.waNumber;
  if (!number) return;

  const lines = [button.dataset.waTitle || ''];

  if (form) {
    form.querySelectorAll('[data-wa-field]').forEach((field) => {
      const value = (field.value || '').trim();
      if (value) lines.push(`${field.dataset.waField}: ${value}`);
    });
  }

  const url = `https://wa.me/${encodeURIComponent(number)}?text=${encodeURIComponent(
    lines.filter(Boolean).join('\n')
  )}`;

  const popup = window.open(url, '_blank', 'noopener');
  const fallback = form && form.querySelector('[data-wa-fallback]');

  if (!popup || popup.closed || typeof popup.closed === 'undefined') {
    // Popup blocked (common on iOS Safari and in in-app browsers)
    if (fallback) {
      fallback.hidden = false;
      fallback.textContent = '';
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.style.textDecoration = 'underline';
      link.textContent = t(
        'whatsapp_blocked',
        'WhatsApp did not open. Tap here to continue.'
      );
      fallback.appendChild(link);
      link.focus();
    }
  } else if (fallback) {
    fallback.hidden = true;
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const routesRoot =
    window.Shopify?.routes?.root ||
    '/';

  const cartRoutes = {
    get: `${routesRoot}cart.js`,
    add: `${routesRoot}cart/add.js`,
    change: `${routesRoot}cart/change.js`,
  };

  /* ── Sticky Header ─────────────────────────── */

  /* One scroll listener for the life of the page; it re-resolves the header
     each time so a replaced header element still gets the class. */
  const onScroll = () => {
    document
      .getElementById('siteHeader')
      ?.classList.toggle('scrolled', window.scrollY > 60);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ── Header stack height ───────────────────────
     Publishes the real height of the announcement bar + header so page
     content is offset correctly at every breakpoint, instead of the single
     hardcoded 68px that ignored the bar and the shorter mobile headers. */

  const syncHeaderStackHeight = () => {
    const headerStack = document.getElementById('headerStack');
    if (!headerStack) return;
    const height = Math.round(
      headerStack.getBoundingClientRect().height
    );
    if (height > 0) {
      document.documentElement.style.setProperty(
        '--header-stack-h',
        `${height}px`
      );
    }
  };

  const headerStack = document.getElementById('headerStack');

  if (headerStack) {
    syncHeaderStackHeight();

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(syncHeaderStackHeight).observe(headerStack);
    } else {
      window.addEventListener('resize', syncHeaderStackHeight, {
        passive: true,
      });
    }

    // Fonts landing late can change the bar height
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncHeaderStackHeight).catch(() => {});
    }
  }

  /* ── Toast Notification ────────────────────── */

  window.showToast = (message, duration = 3000) => {
    let toast = document.getElementById('globalToast');

    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'globalToast';
      toast.className = 'toast';
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('show');

    clearTimeout(toast._timer);

    toast._timer = setTimeout(() => {
      toast.classList.remove('show');
    }, duration);
  };

  /* ── Helpers ───────────────────────────────── */

  const requestJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      throw new Error(
        data?.description ||
        data?.message ||
        'Shopify cart request failed'
      );
    }

    return data;
  };

  const escapeHTML = (value = '') => {
    const div = document.createElement('div');
    div.textContent = String(value);
    return div.innerHTML;
  };

  /* Money formatting.

     Was hardcoded to EGP with ar-EG/en-EG, so any other market showed the
     wrong symbol in the drawer while the cart page (which uses Liquid's
     `money` filter) showed the right one, and sub-unit amounts were rounded
     away in the drawer only.

     Now driven by the shop's own money_format, which is what the cart page
     uses, so the two agree. `money_format` may contain markup — the template
     is applied to text, never injected as HTML. */

  const moneyFormat = window.RGTheme?.moneyFormat || '';

  const formatWithShopFormat = (cents, format) => {
    // Supports Shopify's {{amount}}, {{amount_no_decimals}},
    // {{amount_with_comma_separator}} and {{amount_no_decimals_with_comma_separator}}
    const value = Number(cents) / 100;

    const group = (whole, sep) =>
      whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

    return format.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (_match, token) => {
        const comma = token.indexOf('comma_separator') !== -1;
        const noDecimals = token.indexOf('no_decimals') !== -1;
        const decimals = noDecimals ? 0 : 2;
        const fixed = value.toFixed(decimals);
        const [whole, fraction] = fixed.split('.');

        const thousands = comma ? '.' : ',';
        const decimal = comma ? ',' : '.';

        return (
          group(whole, thousands) +
          (fraction ? decimal + fraction : '')
        );
      }
    );
  };

  const formatMoney = (cents, currencyCode) => {
    if (moneyFormat && moneyFormat.indexOf('{{') !== -1) {
      // Strip any markup the merchant's money format carries, so it can never
      // be rendered as literal text by textContent
      const div = document.createElement('div');
      div.innerHTML = formatWithShopFormat(cents, moneyFormat);
      const text = (div.textContent || '').trim();
      if (text) return text;
    }

    const currency =
      currencyCode || window.RGTheme?.currency || 'USD';

    try {
      return (Number(cents) / 100).toLocaleString(
        window.RGTheme?.locale || undefined,
        { style: 'currency', currency }
      );
    } catch (error) {
      return `${currency} ${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  const setCartCount = (count) => {
    const cartCount = document.getElementById('cartCount');

    if (cartCount) {
      cartCount.textContent = count;
    }
  };

  /* ── Cart Count ────────────────────────────── */

  window.updateCartCount = async () => {
    try {
      const cart = await requestJSON(cartRoutes.get);
      setCartCount(cart.item_count);
    } catch (error) {
      console.error('Unable to update cart count:', error);
    }
  };

  /* ── Cart Drawer Elements ──────────────────── */

  let lastCartFocus = null;
  let lastNavFocus = null;

  let cartDrawer = null;
  let cartOverlay = null;
  let cartItems = null;
  let cartFooter = null;

  const getCartElements = () => {
    cartDrawer = document.getElementById('cartDrawer');
    cartOverlay = document.getElementById('cartOverlay');
    cartItems = document.getElementById('cartItems');
    cartFooter = document.getElementById('cartFooter');
  };

  /* ── Build Drawer Only If Missing ──────────── */

  const buildCartDrawer = () => {
    if (!document.getElementById('cartOverlay')) {
      const overlay = document.createElement('div');

      overlay.id = 'cartOverlay';
      overlay.className = 'cart-drawer-overlay';

      document.body.appendChild(overlay);
    }

    if (!document.getElementById('cartDrawer')) {
      const drawer = document.createElement('div');

      drawer.id = 'cartDrawer';
      drawer.className = 'cart-drawer';

      drawer.innerHTML = `
        <div class="cart-drawer-header">
          <h2 class="cart-drawer-title">
            ${t('cartTitle', 'Cart')}
          </h2>

          <button
            type="button"
            class="cart-drawer-close"
            id="cartClose"
            aria-label="${t('close', 'Close')}"
          >
            ✕
          </button>
        </div>

        <div class="cart-drawer-items" id="cartItems">
          <p style="
            color:var(--muted);
            text-align:center;
            padding:40px 0;
          ">
            ${t('cartEmpty', 'Your cart is empty')}
          </p>
        </div>

        <div
          class="cart-drawer-footer"
          id="cartFooter"
          style="display:none;"
        >
          <div class="cart-total">
            <span class="cart-total-label">
              ${t('cartTotal', 'Total')}
            </span>

            <span
              class="cart-total-price"
              id="cartTotalPrice"
            ></span>
          </div>

          <a
            href="/checkout"
            class="cart-checkout-btn"
            style="
              display:block;
              text-align:center;
              text-decoration:none;
            "
          >
            ${t('checkout', 'Checkout')}
          </a>
        </div>
      `;

      document.body.appendChild(drawer);
    }

    getCartElements();
  };

  /* ── Render Cart Items ─────────────────────── */

  const renderCartItems = (cart) => {
    getCartElements();

    if (!cartItems) {
      return;
    }

    setCartCount(cart.item_count);

    if (cart.item_count === 0) {
      cartItems.innerHTML = `
        <p style="
          color:var(--muted);
          text-align:center;
          padding:40px 0;
        ">
          ${t('cartEmpty', 'Your cart is empty')}
        </p>
      `;

      if (cartFooter) {
        cartFooter.style.display = 'none';
      }

      return;
    }

    cartItems.innerHTML = cart.items
      .map((item, index) => {
        const line = index + 1;
        const productTitle = escapeHTML(item.product_title);
        const variantTitle = escapeHTML(item.variant_title || '');
        const image = item.image
          ? escapeHTML(item.image)
          : '';

        return `
          <div class="cart-item">

            ${
              image
                ? `
                  <a href="${escapeHTML(item.url || '#')}">
                    <img
                      class="cart-item-img"
                      src="${image}"
                      alt="${productTitle}"
                      loading="lazy"
                    >
                  </a>
                `
                : `
                  <div
                    class="cart-item-img"
                    style="background:var(--ivory);"
                  ></div>
                `
            }

            <div style="flex:1;min-width:0;">

              <div class="cart-item-name">
                <a
                  href="${escapeHTML(item.url || '#')}"
                  style="color:inherit;text-decoration:none;"
                >
                  ${productTitle}
                </a>
              </div>

              ${
                variantTitle &&
                variantTitle !== 'Default Title'
                  ? `
                    <div style="
                      font-size:12px;
                      color:var(--muted);
                      margin-bottom:4px;
                    ">
                      ${variantTitle}
                    </div>
                  `
                  : ''
              }

              <div class="cart-item-price">
                ${formatMoney(item.final_price, cart.currency)}
              </div>

              <div class="cart-item-qty">

                <button
                  type="button"
                  class="qty-btn"
                  data-cart-action="quantity"
                  data-line="${line}"
                  data-quantity="${Math.max(0, item.quantity - 1)}"
                  aria-label="${t('decreaseQty', 'Decrease quantity')}"
                >
                  −
                </button>

                <span class="qty-num">
                  ${item.quantity}
                </span>

                <button
                  type="button"
                  class="qty-btn"
                  data-cart-action="quantity"
                  data-line="${line}"
                  data-quantity="${item.quantity + 1}"
                  aria-label="${t('increaseQty', 'Increase quantity')}"
                >
                  +
                </button>

              </div>
            </div>

            <button
              type="button"
              class="cart-item-remove"
              data-cart-action="remove"
              data-line="${line}"
              data-quantity="0"
              aria-label="${t('removeItem', 'Remove')} ${productTitle}"
              style="
                border:0;
                background:none;
                padding:4px;
                cursor:pointer;
                color:var(--muted);
                font-size:15px;
                align-self:flex-start;
              "
            >
              ✕
            </button>

          </div>
        `;
      })
      .join('');

    const totalPrice =
      document.getElementById('cartTotalPrice');

    if (totalPrice) {
      totalPrice.textContent = formatMoney(cart.total_price, cart.currency);
    }

    if (cartFooter) {
      cartFooter.style.display = 'block';
    }
  };

  /* ── Load Cart ─────────────────────────────── */

  const loadCart = async () => {
    const cart = await requestJSON(cartRoutes.get);
    renderCartItems(cart);
    return cart;
  };

  /* ── Open / Close Cart Drawer ──────────────── */

  window.openCartDrawer = async () => {
    buildCartDrawer();

    if (!cartDrawer || !cartOverlay) {
      return;
    }

    // Only one overlay at a time — the two share a stacking level
    if (typeof window.closeMobileNav === 'function') window.closeMobileNav();

    cartDrawer.classList.add('open');
    cartOverlay.classList.add('open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rg-scroll-locked');

    lastCartFocus = document.activeElement;
    const closeBtn = cartDrawer.querySelector('.cart-drawer-close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });

    try {
      await loadCart();
    } catch (error) {
      console.error('Unable to load cart:', error);
      showToast(t('cartLoadError', 'Unable to load cart'));
    }
  };

  window.closeCartDrawer = () => {
    getCartElements();

    const wasOpen = cartDrawer && cartDrawer.classList.contains('open');

    if (cartDrawer) {
      cartDrawer.classList.remove('open');
      cartDrawer.setAttribute('aria-hidden', 'true');
    }

    if (cartOverlay) {
      cartOverlay.classList.remove('open');
    }

    document.body.classList.remove('rg-scroll-locked');

    if (wasOpen && lastCartFocus && lastCartFocus.isConnected) {
      lastCartFocus.focus({ preventScroll: true });
    }
    lastCartFocus = null;
  };

  /* ── Update Cart Quantity ──────────────────── */

  const updateCartLine = async (line, quantity, button = null) => {
    if (!line || quantity < 0) {
      return;
    }

    if (button) {
      button.disabled = true;
    }

    try {
      const cart = await requestJSON(cartRoutes.change, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
        }),
      });

      renderCartItems(cart);
    } catch (error) {
      console.error('Unable to update cart:', error);

      showToast(error.message || t('cartUpdateError', 'Unable to update cart'));

      try {
        await loadCart();
      } catch (loadError) {
        console.error(loadError);
      }
    } finally {
      if (button && button.isConnected) {
        button.disabled = false;
      }
    }
  };

  /* Compatibility with any older inline buttons */

  window.updateQty = (line, quantity) => {
    return updateCartLine(line, quantity);
  };

  /* ── Cart Drawer Events ────────────────────── */

  const bindCartDrawerEvents = () => {
    buildCartDrawer();

    const closeButton =
      document.getElementById('cartClose');

    if (
      closeButton &&
      closeButton.dataset.cartBound !== 'true'
    ) {
      closeButton.addEventListener('click', () => {
        closeCartDrawer();
      });

      closeButton.dataset.cartBound = 'true';
    }

    if (
      cartOverlay &&
      cartOverlay.dataset.cartBound !== 'true'
    ) {
      cartOverlay.addEventListener('click', () => {
        closeCartDrawer();
      });

      cartOverlay.dataset.cartBound = 'true';
    }

    if (
      cartItems &&
      cartItems.dataset.cartBound !== 'true'
    ) {
      cartItems.addEventListener('click', (event) => {
        const button = event.target.closest(
          '[data-cart-action]'
        );

        if (!button) {
          return;
        }

        event.preventDefault();

        const line = Number(button.dataset.line);
        const quantity = Number(button.dataset.quantity);

        updateCartLine(line, quantity, button);
      });

      cartItems.dataset.cartBound = 'true';
    }
  };

  bindCartDrawerEvents();

  /* ── AJAX Add to Cart ──────────────────────── */

  window.addToCart = async (variantId, button) => {
    if (!variantId) {
      return;
    }

    const originalContent = button
      ? button.innerHTML
      : '';

    if (button) {
      button.disabled = true;
      button.textContent = t('adding', 'Adding…');
    }

    try {
      await requestJSON(cartRoutes.add, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
        }),
      });

      showToast(`✓ ${t('addedToCart', 'Added to cart')}`);

      await openCartDrawer();
    } catch (error) {
      console.error('Unable to add product:', error);

      showToast(error.message || t('addError', 'Something went wrong'));
    } finally {
      if (button) {
        button.innerHTML = originalContent;
        button.disabled = false;
      }
    }
  };

  /* ── Cart Icon ─────────────────────────────── */

  /* Delegated so a Theme Editor section reload (which replaces the header DOM)
     cannot leave a dead cart button behind, and so no listener is ever bound
     twice. */
  document.addEventListener('click', (event) => {
    if (event.target.closest('#cartToggle')) {
      event.preventDefault();
      openCartDrawer();
    }
  });

  /* ── Close Cart With Escape ────────────────── */

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeCartDrawer();
      closeMobileNav();
    }
  });

  /* ── Smooth Scrolling ──────────────────────── */

  document
    .querySelectorAll('a[href^="#"]')
    .forEach((link) => {
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');

        if (!href || href === '#') {
          return;
        }

        const target = document.querySelector(href);

        if (!target) {
          return;
        }

        event.preventDefault();

        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    });

  /* ── Mobile Navigation ─────────────────────── */

  /* Looked up on demand rather than captured once, so the references survive a
     Theme Editor section reload. */
  const navEls = () => ({
    button: document.getElementById('mobileMenuBtn'),
    nav: document.getElementById('mobileNav'),
    overlay: document.getElementById('mobileNavOverlay'),
  });

  document.addEventListener('click', (event) => {
    const { button, nav, overlay } = navEls();

    if (button && event.target.closest('#mobileMenuBtn')) {
      if (typeof window.closeCartDrawer === 'function') window.closeCartDrawer();

      nav?.classList.add('open');
      overlay?.classList.add('open');
      nav?.setAttribute('aria-hidden', 'false');
      button.setAttribute('aria-expanded', 'true');
      document.body.classList.add('rg-scroll-locked');

      lastNavFocus = document.activeElement;
      nav?.querySelector('.mobile-nav-close')?.focus({ preventScroll: true });
      return;
    }

    if (overlay && event.target === overlay) closeMobileNav();
  });

  window.closeMobileNav = () => {
    const { button, nav, overlay } = navEls();
    const wasOpen = nav && nav.classList.contains('open');

    nav?.classList.remove('open');
    overlay?.classList.remove('open');
    nav?.setAttribute('aria-hidden', 'true');
    button?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('rg-scroll-locked');

    if (wasOpen && lastNavFocus && lastNavFocus.isConnected) {
      lastNavFocus.focus({ preventScroll: true });
    }
    lastNavFocus = null;
  };

  /* ── English Direction ─────────────────────── */

  if (
    window.RGTheme &&
    !window.RGTheme.isRTL
  ) {
    document.body.classList.add('lang-en');
    document.documentElement.setAttribute('dir', 'ltr');
  }

  /* ── Address country/province ───────────────────
     country_option_tags emits a data-provinces JSON array on each option;
     read it directly rather than pulling in Shopify's deprecated
     shopify_common.js. */

  document.querySelectorAll('[data-address-country]').forEach((countrySelect) => {
    const wrapper = countrySelect
      .closest('.sr-form-row')
      ?.querySelector('[id^="Address_ProvinceWrapper_"]');
    const provinceSelect = wrapper?.querySelector('[data-address-province]');
    if (!wrapper || !provinceSelect) return;

    const syncProvinces = () => {
      const option = countrySelect.options[countrySelect.selectedIndex];
      let provinces = [];

      try {
        provinces = JSON.parse(option?.dataset.provinces || '[]');
      } catch (error) {
        provinces = [];
      }

      provinceSelect.textContent = '';

      if (!provinces.length) {
        wrapper.style.display = 'none';
        return;
      }

      provinces.forEach(([value, label]) => {
        const opt = document.createElement('option');
        opt.value = value;
        opt.textContent = label;
        provinceSelect.appendChild(opt);
      });

      if (provinceSelect.dataset.default) {
        provinceSelect.value = provinceSelect.dataset.default;
      }
      wrapper.style.display = '';
    };

    if (countrySelect.dataset.default) {
      countrySelect.value = countrySelect.dataset.default;
    }
    countrySelect.addEventListener('change', syncProvinces);
    syncProvinces();
  });

  /* ── Initial Cart Count ────────────────────── */

  updateCartCount();

  /* ── Theme Editor section reloads ─────────────
     Re-run the parts that read the DOM. Everything else is delegated at the
     document level, so nothing is bound twice. */

  document.addEventListener('shopify:section:load', () => {
    syncHeaderStackHeight();
    onScroll();
    getCartElements();
    bindCartDrawerEvents();
  });

  document.addEventListener('shopify:section:unload', () => {
    syncHeaderStackHeight();
    closeMobileNav();
  });
});
/* ── Horizontal carousels (reviews + categories) ──
   Replaces the previous reviews implementation, which moved slides with
   Element.scrollIntoView(). That was the cause of the page jumping to the
   Happy Clients section on load: scrollIntoView scrolls EVERY scrollable
   ancestor, including the document. `block:'nearest'` only asks for the
   minimum vertical scrolling needed to reveal the element — with the reviews
   section below the fold that minimum is scrolling the whole page down. The
   autoplay timer then did it unprompted a few seconds after load.

   Nothing here touches window/document scroll. The only scroll call is
   track.scrollTo({ left }), which moves the track's own overflow box and
   cannot move the page. Focus is never moved programmatically. */
(function () {
  const instances = new Map();

  /* Direction-agnostic horizontal move.
     The delta is measured from bounding rects and ADDED to the track's
     current scrollLeft, so it is correct in both LTR and RTL regardless of
     whether the browser reports RTL scrollLeft as positive or negative. */
  const moveTo = (track, slide, smooth) => {
    const t = track.getBoundingClientRect();
    const s = slide.getBoundingClientRect();
    const rtl = getComputedStyle(track).direction === 'rtl';
    const delta = rtl ? s.right - t.right : s.left - t.left;
    track.scrollTo({
      left: track.scrollLeft + delta,
      behavior: smooth ? 'smooth' : 'auto'
    });
  };

  const leadingIndex = (track, slides) => {
    const t = track.getBoundingClientRect();
    const rtl = getComputedStyle(track).direction === 'rtl';
    const edge = rtl ? t.right : t.left;
    let best = 0;
    let bestDistance = Infinity;
    slides.forEach((slide, i) => {
      const r = slide.getBoundingClientRect();
      const d = Math.abs((rtl ? r.right : r.left) - edge);
      if (d < bestDistance) { bestDistance = d; best = i; }
    });
    return best;
  };

  function init(root) {
    if (instances.has(root)) return;

    const name = root.dataset.carousel;                 // 'reviews' | 'categories'
    const track = root.querySelector('[data-carousel-track]');
    if (!track) return;
    const slides = Array.from(root.querySelectorAll('[data-carousel-slide]'));
    if (!slides.length) return;

    const prevBtn = root.querySelector('[data-carousel-prev]');
    const nextBtn = root.querySelector('[data-carousel-next]');
    const status = root.querySelector('[data-carousel-status]');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const controller = new AbortController();
    const { signal } = controller;
    const wantsAutoplay = root.dataset.carouselAutoplay === 'true';
    const interval = Math.max(3000, parseInt(root.dataset.carouselInterval, 10) || 6000);

    let timer = null;
    let inView = false;
    let hovered = false;
    let focusWithin = false;
    let dragging = false;

    /* ── Prev / next enablement ───────────────── */
    const syncButtons = () => {
      if (!prevBtn && !nextBtn) return;
      const t = track.getBoundingClientRect();
      const rtl = getComputedStyle(track).direction === 'rtl';
      const first = slides[0].getBoundingClientRect();
      const last = slides[slides.length - 1].getBoundingClientRect();
      const atStart = rtl ? first.right <= t.right + 2 : first.left >= t.left - 2;
      const atEnd = rtl ? last.left >= t.left - 2 : last.right <= t.right + 2;
      if (prevBtn) prevBtn.disabled = atStart;
      if (nextBtn) nextBtn.disabled = atEnd;
    };

    const goTo = (target, announce) => {
      const i = Math.max(0, Math.min(slides.length - 1, target));
      moveTo(track, slides[i], !motionQuery.matches);
      /* Only user-driven moves touch the live region. Autoplay announcing
         every few seconds would talk over the rest of the page. Focus is
         never moved — that would scroll the page too. */
      if (announce && status) {
        status.textContent = slides[i].getAttribute('aria-label') || '';
      }
    };

    const step = (dir, announce) => goTo(leadingIndex(track, slides) + dir, announce);

    /* ── Autoplay ─────────────────────────────── */
    const canAutoplay = () =>
      wantsAutoplay &&
      !motionQuery.matches &&
      inView &&
      !hovered &&
      !focusWithin &&
      !dragging &&
      !document.hidden;

    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

    const start = () => {
      stop();
      if (!canAutoplay()) return;
      timer = setInterval(() => {
        if (!canAutoplay()) { stop(); return; }
        const i = leadingIndex(track, slides);
        const t = track.getBoundingClientRect();
        const rtl = getComputedStyle(track).direction === 'rtl';
        const last = slides[slides.length - 1].getBoundingClientRect();
        const atEnd = rtl ? last.left >= t.left - 2 : last.right <= t.right + 2;
        goTo(atEnd ? 0 : i + 1, false);   // wrap by scrolling the track only
      }, interval);
    };

    const restart = () => { stop(); start(); };

    /* Autoplay only runs while the section is genuinely on screen, so a
       carousel far below the fold never animates and never competes for
       resources. 35% keeps it from starting on a sliver. */
    let observer = null;
    if (wantsAutoplay && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
          restart();
        });
      }, { threshold: [0, 0.35, 0.6] });
      observer.observe(root);
    } else if (wantsAutoplay) {
      inView = true;                       // no IO support: fall back to always-on
    }

    if (prevBtn) prevBtn.addEventListener('click', () => { step(-1, true); restart(); }, { signal });
    if (nextBtn) nextBtn.addEventListener('click', () => { step(1, true); restart(); }, { signal });

    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();             // stops the arrow keys scrolling the page
      const rtl = getComputedStyle(track).direction === 'rtl';
      const forward = rtl ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
      step(forward ? 1 : -1, true);
      restart();
    }, { signal });

    root.addEventListener('pointerenter', () => { hovered = true; stop(); }, { signal });
    root.addEventListener('pointerleave', () => { hovered = false; start(); }, { signal });
    root.addEventListener('focusin', () => { focusWithin = true; stop(); }, { signal });
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) { focusWithin = false; start(); }
    }, { signal });
    track.addEventListener('touchstart', () => { dragging = true; stop(); }, { passive: true, signal });
    track.addEventListener('touchend', () => { dragging = false; start(); }, { passive: true, signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop(); else start();
    }, { signal });
    motionQuery.addEventListener('change', restart, { signal });

    track.addEventListener('scroll', syncButtons, { passive: true, signal });
    window.addEventListener('resize', syncButtons, { signal });

    /* ── Pointer drag ─────────────────────────── */
    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragMoved = 0;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragging = true;
      dragMoved = 0;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
      stop();
    }, { signal });
    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - dragStartX;
      if (Math.abs(dx) > 3) {
        dragMoved = Math.abs(dx);
        track.classList.add('is-dragging');
        track.scrollLeft = dragStartScroll - dx;
      }
    }, { signal });
    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      syncButtons();
      start();
    };
    track.addEventListener('pointerup', endDrag, { signal });
    track.addEventListener('pointercancel', endDrag, { signal });
    track.addEventListener('pointerleave', endDrag, { signal });
    /* A drag that ends on a card must not also follow the card's link. */
    track.addEventListener('click', (event) => {
      if (dragMoved > 6) { event.preventDefault(); event.stopPropagation(); }
      dragMoved = 0;
    }, { capture: true, signal });

    instances.set(root, () => {
      stop();
      if (observer) observer.disconnect();
      controller.abort();
    });

    syncButtons();
    start();
    if (name === 'categories') root.classList.add('is-ready');
  }

  const initAll = (scope) =>
    (scope || document).querySelectorAll('[data-carousel]').forEach(init);

  const destroy = (scope) => {
    instances.forEach((teardown, root) => {
      if (!document.contains(root) || (scope && scope.contains(root))) {
        teardown();
        instances.delete(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* Theme Editor reloads: tear the old instance down before building a new
     one, so intervals and observers can never stack up. */
  document.addEventListener('shopify:section:load', (event) => {
    destroy(event.target);
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
